/**
 * The decisions behind the Abby welcome tab, as pure functions
 * (spec 2026-10-23 §4.2, §5 Tab 2; rulings R19, R20). The React components
 * only draw what these return, so every rule is unit-tested without a DOM
 * (tests/unit/admin/app-experience-welcome-state.test.ts).
 *
 * Kept apart, as on the intro tab:
 *   - CONTENT (mode, text, Abby's voice, videos) is a DRAFT until Publish.
 *   - ON/OFF is the switch. It publishes the newest version with `enabled`
 *     flipped, never the draft. Publish keeps the current on/off state.
 *
 * Two facts of the welcome that the intro does not have:
 *   - Nothing published = the welcome is ON with the built-in text and voice
 *     (spec §4.2 "Unchanged when nothing is published").
 *   - The voice is all or none (contract: `voice` is both languages or null).
 *     "Built-in" = the TRIMMED text equals the phone's split in BOTH languages,
 *     and then Publish sends `voice: null`. Otherwise BOTH languages need the
 *     voice that assemble returned for exactly that text (R19). Publish sends
 *     what assemble RETURNED, never the draft.
 */
import {
  validateAbbyWelcomeConfig,
  welcomeScriptHash,
  type AbbyWelcomeConfig,
  type ExperienceLocale,
  type VideoAsset,
  type WelcomeVoiceLocale,
} from './contract';
import { LANGUAGE_NAME, LOCALES, type FinalizedMedia, type IntroStatus } from './intro-state';
import { formatDuration, shapeLabel } from './video-client-checks';
import { builtInText, isBuiltInText, sameParagraphs, textProblems, trimParagraphs } from './welcome-text';
import { formatClock } from './welcome-sync';

export type WelcomeMode = AbbyWelcomeConfig['mode'];

export interface WelcomeDraft {
  mode: WelcomeMode;
  /** The paragraphs as typed (not trimmed). */
  text: Record<ExperienceLocale, string[]>;
  videos: Record<ExperienceLocale, FinalizedMedia | null>;
  /** Kept apart from the video so a replaced video keeps the text the admin wrote. */
  descriptions: Record<ExperienceLocale, string>;
}

/** The voice assemble returned for each language (or the published one); null = none yet. */
export type GeneratedVoice = Record<ExperienceLocale, WelcomeVoiceLocale | null>;

const READ_ONLY = 'You can only view this page.';
export const REGENERATE = "Generate Abby's voice again — the text changed.";
const GENERATE = "Generate Abby's voice for this text.";
const GENERATE_TOO = "Generate Abby's voice for this text too. When one language has new text, both languages need Abby's voice.";
export const ADD_ENGLISH_VIDEO = 'Add the English video.';

/**
 * Ruling R20a: in video mode the text editor is hidden; the draft stays in the
 * page state. The note says the three facts the admin needs.
 */
export const NOTE_FOR_VIDEO_MODE =
  'In video mode, members see the video. Older app versions show the built-in text. Your text is kept on this page: it comes back when you choose "Abby reads the text".';

/**
 * Words for a config that the shared validator refuses (an unexpected state,
 * for example a file on another media host). They name what to do in THIS
 * mode: in video mode they never speak of Abby's voice.
 */
export function cannotPublishText(config: AbbyWelcomeConfig): string {
  if (config.mode === 'video') {
    return 'This version cannot be published because one of its files is not valid. Reload the page, upload the video again, then publish.';
  }
  if (config.voice) {
    return "This version cannot be published because one of its files is not valid. Reload the page, generate Abby's voice again, then publish.";
  }
  return 'This version cannot be published because it is not valid. Reload the page, then try again.';
}

/** A stored version, read through the shared validator. Never throws; null = cannot read it. */
export function welcomeConfigOf(raw: unknown): AbbyWelcomeConfig | null {
  try {
    const r = validateAbbyWelcomeConfig(raw, null);
    return r.ok ? r.value : null;
  } catch {
    return null;
  }
}

function mediaOf(v: VideoAsset | null | undefined): FinalizedMedia | null {
  if (!v) return null;
  const { description: _description, ...media } = v;
  return media;
}

export function welcomeDraftFrom(live: AbbyWelcomeConfig | null): WelcomeDraft {
  const voice = live?.voice ?? null;
  return {
    mode: live?.mode ?? 'voice',
    text: {
      en: voice ? [...voice.en.paragraphs] : builtInText('en'),
      es: voice ? [...voice.es.paragraphs] : builtInText('es'),
    },
    videos: { en: mediaOf(live?.videos.en), es: mediaOf(live?.videos.es) },
    descriptions: { en: live?.videos.en?.description ?? '', es: live?.videos.es?.description ?? '' },
  };
}

/** The published voice counts as generated: it matches the published text by construction. */
export function generatedFrom(live: AbbyWelcomeConfig | null): GeneratedVoice {
  return { en: live?.voice?.en ?? null, es: live?.voice?.es ?? null };
}

/**
 * The voice that counts for each language: the one that MATCHES the draft
 * text, made on this page or published (fix round 1, item 2). So an admin who
 * edits, generates, then types the published text back gets the published
 * voice again, and no "text changed" error. When neither matches, the voice
 * made here is kept, so the preview can say that it is for older text.
 */
export function voiceForDraft(
  draft: WelcomeDraft,
  madeHere: Partial<Record<ExperienceLocale, WelcomeVoiceLocale>>,
  published: GeneratedVoice,
): GeneratedVoice {
  const pick = (loc: ExperienceLocale): WelcomeVoiceLocale | null => {
    const mine = madeHere[loc] ?? null;
    if (voiceMatches(mine, draft.text[loc])) return mine;
    if (voiceMatches(published[loc], draft.text[loc])) return published[loc];
    return mine ?? published[loc];
  };
  return { en: pick('en'), es: pick('es') };
}

/** The voice matches this text: the hash of the TRIMMED paragraphs, as the server computes it. */
export function voiceMatches(voice: WelcomeVoiceLocale | null, paragraphs: readonly string[]): boolean {
  if (!voice) return false;
  const trimmed = trimParagraphs(paragraphs);
  return voice.script_hash === welcomeScriptHash(trimmed) && sameParagraphs(voice.paragraphs, trimmed);
}

// ─── Publish: the brief's rule ──────────────────────────────────────────────

export interface WelcomePublishCheck {
  canPublish: boolean;
  /** Every reason. A reason about one language starts with its name ("English: …"). */
  reasons: string[];
  /** The text and voice reasons of each language, without the language name. */
  byLocale: Record<ExperienceLocale, string[]>;
  /** What Publish sends as `voice` (null = built-in text and voice). Meaningful only when canPublish. */
  voice: Record<ExperienceLocale, WelcomeVoiceLocale> | null;
}

export function welcomePublishState(draft: WelcomeDraft, generated: GeneratedVoice): WelcomePublishCheck {
  const byLocale: Record<ExperienceLocale, string[]> = { en: [], es: [] };

  if (draft.mode === 'video') {
    // R20: the text is not published in video mode; the English video is required.
    const reasons = draft.videos.en ? [] : [ADD_ENGLISH_VIDEO];
    return { canPublish: reasons.length === 0, reasons, byLocale, voice: null };
  }

  for (const loc of LOCALES) byLocale[loc].push(...textProblems(draft.text[loc]));
  const textOk = LOCALES.every((loc) => byLocale[loc].length === 0);
  const builtIn = LOCALES.map((loc) => isBuiltInText(draft.text[loc], loc));

  if (textOk && builtIn.every(Boolean)) {
    return { canPublish: true, reasons: [], byLocale, voice: null };
  }
  if (textOk) {
    for (const [i, loc] of LOCALES.entries()) {
      const g = generated[loc];
      if (voiceMatches(g, draft.text[loc])) continue;
      if (g) byLocale[loc].push(REGENERATE);
      else byLocale[loc].push(builtIn[i] ? GENERATE_TOO : GENERATE);
    }
  }
  const reasons = LOCALES.flatMap((loc) => byLocale[loc].map((r) => `${LANGUAGE_NAME[loc]}: ${r}`));
  const canPublish = reasons.length === 0;
  const voice = canPublish ? { en: generated.en as WelcomeVoiceLocale, es: generated.es as WelcomeVoiceLocale } : null;
  return { canPublish, reasons, byLocale, voice };
}

/** The config Publish sends. `enabled` comes from the caller (the current on/off state). */
export function welcomeConfigFrom(
  draft: WelcomeDraft,
  voice: Record<ExperienceLocale, WelcomeVoiceLocale> | null,
  enabled: boolean,
): AbbyWelcomeConfig {
  const video = (loc: ExperienceLocale): VideoAsset | null => {
    const m = draft.videos[loc];
    if (!m) return null;
    const text = draft.descriptions[loc].trim();
    return { ...m, description: text === '' ? null : text };
  };
  return {
    schema: 1,
    enabled,
    mode: draft.mode,
    voice: draft.mode === 'video' ? null : voice,
    videos: { en: video('en'), es: video('es') },
  };
}

// ─── what Generate will do ──────────────────────────────────────────────────

export interface VoicePlan {
  /** The languages Generate sends, in order. Empty = nothing to do. */
  locales: ExperienceLocale[];
  /** Why Generate cannot run; null when it can. */
  blocked: string | null;
  /** One sentence about what Generate will do, or why there is nothing to do. */
  note: string;
}

function languageList(locales: ExperienceLocale[]): string {
  return locales.map((l) => LANGUAGE_NAME[l]).join(' and ');
}

export function voicePlan(draft: WelcomeDraft, generated: GeneratedVoice): VoicePlan {
  const bad = LOCALES.filter((loc) => textProblems(draft.text[loc]).length > 0);
  if (bad.length > 0) {
    return { locales: [], blocked: `Correct the problems in the ${languageList(bad)} text first.`, note: '' };
  }
  if (LOCALES.every((loc) => isBuiltInText(draft.text[loc], loc))) {
    return {
      locales: [],
      blocked: null,
      note: 'The text is the built-in text in both languages. Members hear the built-in voice. There is nothing to generate.',
    };
  }
  const locales = LOCALES.filter((loc) => !voiceMatches(generated[loc], draft.text[loc]));
  if (locales.length === 0) return { locales, blocked: null, note: "Abby's voice matches the text in both languages." };
  return {
    locales,
    blocked: null,
    note: `Abby reads the ${languageList(locales)} text, one paragraph at a time. A paragraph that did not change reuses its voice: no cost and no wait.`,
  };
}

// ─── the tab ────────────────────────────────────────────────────────────────

/** Same shape as IntroPublishState where PublishBar reads it. */
export interface WelcomeTabState {
  /** The draft differs from the newest published version (text included). */
  dirty: boolean;
  canPublish: boolean;
  errors: string[];
  warnings: string[];
  blockedReason: string | null;
  /** What Publish sends; null when it cannot. */
  config: AbbyWelcomeConfig | null;
  /** In video mode, the text was changed but is not sent. */
  textNotSent: boolean;
  check: WelcomePublishCheck;
}

interface Normalized {
  mode: WelcomeMode;
  text: Record<ExperienceLocale, string[]> | null;
  videos: Record<ExperienceLocale, FinalizedMedia | null>;
  descriptions: Record<ExperienceLocale, string>;
}

/** The parts of a draft that Publish sends. In video mode the text is not sent. */
function normalized(d: WelcomeDraft, withText: boolean): Normalized {
  return {
    mode: d.mode,
    text: withText ? { en: trimParagraphs(d.text.en), es: trimParagraphs(d.text.es) } : null,
    videos: d.videos,
    descriptions: { en: d.descriptions.en.trim(), es: d.descriptions.es.trim() },
  };
}

/** JSON with sorted keys, so key order never reads as a change. */
function stable(x: unknown): string {
  if (Array.isArray(x)) return `[${x.map(stable).join(',')}]`;
  if (x && typeof x === 'object') {
    const o = x as Record<string, unknown>;
    return `{${Object.keys(o)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stable(o[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(x) ?? 'null';
}

const same = (a: unknown, b: unknown) => stable(a) === stable(b);

export function welcomeTabState(args: {
  live: AbbyWelcomeConfig | null;
  draft: WelcomeDraft;
  generated: GeneratedVoice;
  canWrite: boolean;
  /** `media_origin` of the admin view: the server checks every media URL against it. */
  mediaOrigin: string | null;
}): WelcomeTabState {
  const { live, draft, generated, canWrite, mediaOrigin } = args;
  const baseline = welcomeDraftFrom(live);
  const dirty = !same(normalized(draft, true), normalized(baseline, true));
  const sendsText = draft.mode === 'voice';
  // Video mode: Publish sends no text, so text edits alone are nothing to publish.
  const changed = !same(normalized(draft, sendsText), normalized(baseline, sendsText));
  const textNotSent = !sendsText && !same(normalized(draft, true).text, normalized(baseline, true).text);

  const check = welcomePublishState(draft, generated);
  const errors = [...check.reasons];
  const warnings: string[] = [];
  if (draft.mode === 'video') {
    // R20: video mode publishes `voice: null`, so a published voice of your own is not in the new version.
    if (live?.voice) {
      warnings.push(
        "In video mode, the new version does not keep your text and Abby's voice. To use them again, restore an older version.",
      );
    }
    for (const loc of LOCALES) {
      if (draft.videos[loc] && draft.descriptions[loc].trim() === '') {
        warnings.push(
          `The ${LANGUAGE_NAME[loc]} description is empty. People who use a screen reader will not know what the video shows.`,
        );
      }
    }
  }

  // Publish keeps the on/off state. Nothing published = on (spec §4.2).
  let config = check.canPublish ? welcomeConfigFrom(draft, check.voice, live?.enabled ?? true) : null;
  if (config) {
    // The SAME validator the server runs, with the server's media origin (R19).
    const v = validateAbbyWelcomeConfig(config, mediaOrigin);
    if (!v.ok) {
      errors.push(cannotPublishText(config));
      config = null;
    }
  }

  const canPublish = canWrite && changed && errors.length === 0 && config !== null;
  let blockedReason: string | null = null;
  if (!canWrite) blockedReason = READ_ONLY;
  else if (errors.length > 0) blockedReason = 'Correct the problems above before you publish.';
  else if (!changed && textNotSent) {
    blockedReason = 'In video mode, members see the video, not the text. Change the mode or a video to publish.';
  } else if (!changed) blockedReason = 'Nothing changed. Change the text, the mode or a video to publish.';
  return { dirty, canPublish, errors, warnings, blockedReason, config, textNotSent, check };
}

// ─── status, switch, restore, history ───────────────────────────────────────

/** Nothing published = on: members get the built-in welcome. "Live" = on for members (R17). */
export function welcomeStatusOf(live: AbbyWelcomeConfig | null): IntroStatus {
  return live === null || live.enabled ? 'live' : 'off';
}

/**
 * Your text and Abby's voice reach a NEW member only from app 1.0.5 (spec
 * 2026-10-23 §6.1): a fresh install of an older binary runs its own JS on the
 * first launch and plays the built-in welcome. Said wherever the page says
 * what members get, like the video-mode note.
 */
export const CUSTOM_TEXT_REACH = 'Your text and voice play on app 1.0.5 and later. Older app versions show the built-in welcome.';

/** What new members get now, in one sentence (two for your own text). */
export function welcomeNowText(live: AbbyWelcomeConfig | null): string {
  if (!live) return 'Nothing is published yet. New members get the built-in welcome: Abby reads the built-in text.';
  if (!live.enabled) return 'New members do not see the Abby welcome.';
  if (live.mode === 'video') return 'New members get the Abby welcome: a video. Older app versions show the built-in text.';
  return live.voice
    ? `New members get the Abby welcome: Abby reads your text. ${CUSTOM_TEXT_REACH}`
    : 'New members get the Abby welcome: Abby reads the built-in text.';
}

export interface WelcomeSwitchState {
  on: boolean;
  canToggle: boolean;
  reason: string | null;
  /** What the switch publishes: the newest version with `enabled` flipped. */
  next: AbbyWelcomeConfig;
}

export function welcomeSwitchState(args: { live: AbbyWelcomeConfig | null; canWrite: boolean }): WelcomeSwitchState {
  const { live, canWrite } = args;
  const on = live?.enabled ?? true;
  // Never published: turning off publishes the built-in welcome, off.
  const next: AbbyWelcomeConfig = live
    ? { ...live, enabled: !on }
    : { schema: 1, enabled: false, mode: 'voice', voice: null, videos: { en: null, es: null } };
  return { on, canToggle: canWrite, reason: canWrite ? null : READ_ONLY, next };
}

export interface WelcomeRestorePlan {
  config: AbbyWelcomeConfig;
  based_on_version: number;
  effects: string[];
}

export function welcomeRestorePlan(args: {
  config: AbbyWelcomeConfig;
  latestVersion: number;
  live: AbbyWelcomeConfig | null;
  dirty: boolean;
}): WelcomeRestorePlan {
  const { config, latestVersion, live, dirty } = args;
  const effects: string[] = [];
  const wasOn = live?.enabled ?? true;
  if (config.enabled && !wasOn) effects.push('This turns the Abby welcome on.');
  if (!config.enabled && wasOn) effects.push('This turns the Abby welcome off.');
  if (dirty) effects.push('Your changes that are not published will be lost.');
  return { config, based_on_version: latestVersion, effects };
}

/** One line for the history: the mode and what each language holds. */
export function welcomeHistorySummary(c: AbbyWelcomeConfig): string {
  if (c.mode === 'video') {
    const v = (loc: ExperienceLocale) => {
      const a = c.videos[loc];
      return a
        ? `${LANGUAGE_NAME[loc]} ${formatDuration(a.duration_ms)}, ${shapeLabel(a.width, a.height)}`
        : `${LANGUAGE_NAME[loc]}: none`;
    };
    return `Video · ${v('en')} · ${v('es')}`;
  }
  if (!c.voice) return 'Abby reads the built-in text';
  const voice = c.voice;
  const v = (loc: ExperienceLocale) => {
    const n = voice[loc].paragraphs.length;
    // The player's clock (rounded down), so the history and the preview show the same length.
    return `${LANGUAGE_NAME[loc]}: ${n} ${n === 1 ? 'paragraph' : 'paragraphs'}, ${formatClock(voice[loc].timing.totalDurationMs)}`;
  };
  return `Abby reads your text · ${v('en')} · ${v('es')}`;
}

/** The first poster of a version, for the history thumbnail (video mode only). */
export function welcomePosterOf(c: AbbyWelcomeConfig): string | null {
  return c.mode === 'video' ? (c.videos.en?.poster_url ?? null) : null;
}
